// Live event store for one job (DESIGN_SPEC §12.1–12.2; implementation.md §5.3).
// SSE pushes land in a queue; one flush per animation frame (every 250 ms while the tab is
// hidden) dedupes by seq, keeps seq order, updates every derivation incrementally and
// notifies React once. Components read it with useSyncExternalStore and memoize on
// `snap.version`, never on array identity (the events array is one instance, append-mostly).
//   createEventStore()          push · end · flushNow · setConnection · subscribe · getSnapshot · rate
//                               (rate.perSec, rate.lastEventAt, rate.series(now, secs): per-second counts by event time,
//                               kept as a histogram so the cockpit's sparkline never walks the whole stream)
//   deriveEffective(snap, job)  effective state/verdict/T0/transitions: the stream beats the lagging
//                               record, except that a terminal record is final
//   TERMINAL · isTerminal · asEffective · verdictLabelFor(score) · verdictFromRecord(job.verdict)
import { categoryOf, emptyCounts } from "./derive/categories";
import type { Category, StreamConnection, Transition, VerdictLabel, VerdictSnap } from "@/lib/derive/types";
import type { EffectiveState, Event, Job, JobState, Tag, Verdict } from "@/types";

type JobEvent = Event;

/** Live process preview keyed by pid (the full tree is derive/tree). */
type LiveProc = { pid: number; ppid: number; image: string; cmdline: string; startSeq: number; exitCode?: number; children: number[] };

/** Live indicator preview ("so far"); kinds match behavior-report.json. */
type IocPreviewKind = "domain" | "ipv4" | "ipv6" | "url" | "file_path" | "registry_key" | "mutex" | "sha256";
export type IocPreview = { kind: IocPreviewKind; value: string; hits: number; firstSeq: number; sources: string[] };

type StreamStateEvent = { state: string; reason: string; seq: number; ts: number };

export type Snapshot = {
  version: number;
  events: readonly JobEvent[];
  counts: Readonly<Record<Category, number>>;
  /** The job.state event with the highest seq (raw state string). */
  state: StreamStateEvent | null;
  /** Lifecycle from job.state events, seq order, repeats collapsed. */
  transitions: readonly Transition[];
  verdict: VerdictSnap | null; // the verdict event with the highest seq
  procs: ReadonlyMap<number, LiveProc>;
  iocs: ReadonlyMap<string, IocPreview>; // key = kind + "\0" + value
  ended: boolean; // the run is over and the stream said `end`
  batchId: number;
  lastSeq: number;
  firstTs: number | null; // event-time bounds (epoch ms)
  lastTs: number | null;
  connection: StreamConnection;
  retry: { attempt: number; at: number } | null;
};

export const TERMINAL: ReadonlySet<EffectiveState> = new Set<EffectiveState>(["completed", "failed", "cancelled"]);
export const isTerminal = (s: string | undefined | null): boolean => !!s && TERMINAL.has(s as EffectiveState);

const KNOWN_STATES: ReadonlySet<string> = new Set<EffectiveState>([
  "queued", "dispatched", "provisioning", "booting", "running", "collecting", "completed", "failed", "cancelled",
]);
export const asEffective = (s: string | undefined | null): EffectiveState | null =>
  s && KNOWN_STATES.has(s) ? (s as EffectiveState) : null;

/** computeVerdict bands: benign 0–24, suspicious 25–59, malicious 60–100. */
export const verdictLabelFor = (score: number): VerdictLabel => (score >= 60 ? "malicious" : score >= 25 ? "suspicious" : "benign");

const str = (v: unknown): string => (typeof v === "string" ? v : v == null ? "" : String(v));
const IPV4 = /^(?:\d{1,3}\.){3}\d{1,3}$/;
const RATE_WINDOW_MS = 5000;
/** Seconds of per-second counts kept for the sparkline (it shows 60). */
const HISTOGRAM_KEEP = 300;

function asLabel(v: unknown, score: number): VerdictLabel {
  return v === "benign" || v === "suspicious" || v === "malicious" ? v : verdictLabelFor(score);
}

function asTags(v: unknown): Tag[] {
  if (!Array.isArray(v)) return [];
  return v.flatMap((t): Tag[] => {
    // Live-stream payload: full Tag objects.
    if (t && typeof t === "object" && typeof (t as Tag).id === "string") return [t as Tag];
    // Persisted compact record: just the id string. Build a stub Tag so
    // downstream readers that only look at .id still work; title falls
    // back to the id so the UI shows something.
    if (typeof t === "string") return [{ id: t, title: t, description: "", severity: 1, hits: 0 }];
    return [];
  });
}

export function verdictFromRecord(v: Verdict): VerdictSnap {
  const score = Number(v.score) || 0;
  return {
    label: asLabel(v.label, score),
    score,
    maxSeverity: str(v.max_severity),
    tagCount: Number(v.tag_count) || 0,
    iocCount: v.ioc_count,
    tags: asTags(v.tags),
    seq: 0,
    source: "record",
  };
}

function verdictFromEvent(fields: Record<string, unknown>, seq: number): VerdictSnap {
  // verdict_updated's wire payload is {state?, reason?, verdict: {...}} —
  // the verdict fields we care about (label, score, tags, …) sit on the
  // nested `verdict` object, NOT on the root. api.ts spreads the whole
  // payload into Event.fields, so unwrap it here. Pre-fix, every event
  // produced {label:"benign", score:0} because f.score/f.label were
  // always undefined and asLabel fell back to verdictLabelFor(0).
  const inner = (fields.verdict && typeof fields.verdict === "object" ? (fields.verdict as Record<string, unknown>) : fields);
  const f = inner;
  const score = Number(f.score) || 0;
  const tags = asTags(f.tags);
  return {
    label: asLabel(f.label, score),
    score,
    maxSeverity: str(f.max_severity),
    tagCount: f.tag_count != null ? Number(f.tag_count) || 0 : tags.length,
    iocCount: f.ioc_count != null && Number.isFinite(Number(f.ioc_count)) ? Number(f.ioc_count) : undefined,
    tags,
    seq,
    source: "stream",
  };
}

/** Untrusted SSE payloads: keep only well-formed events. */
function sane(e: unknown): e is JobEvent {
  if (!e || typeof e !== "object") return false;
  const x = e as Partial<JobEvent>;
  return typeof x.seq === "number" && Number.isFinite(x.seq) && typeof x.kind === "string" && typeof x.ts === "string";
}

export type CreateStoreOptions = {
  /** Receipt clock (tests). */
  now?: () => number;
  /** Flush scheduler (tests); defaults to rAF, or a 250 ms timer while the tab is hidden. */
  schedule?: (flush: () => void) => void;
};

export function createEventStore(opts: CreateStoreOptions = {}) {
  const now = opts.now ?? Date.now;
  const events: JobEvent[] = [];
  const seen = new Set<number>();
  const counts = emptyCounts();
  const procs = new Map<number, LiveProc>();
  const iocs = new Map<string, IocPreview>();
  const stateEvents: Transition[] = [];
  let latestState: StreamStateEvent | null = null;
  let transitions: readonly Transition[] = [];
  let verdict: VerdictSnap | null = null;
  let ended = false;
  let batchId = 0;
  let lastSeq = 0;
  let firstTs: number | null = null;
  let lastTs: number | null = null;
  let connection: StreamConnection = "connecting";
  let retry: Snapshot["retry"] = null;
  // Receipt-capped event times of fresh events (rate window) and the newest event time.
  const recent: number[] = [];
  let lastEventAt: number | null = null;
  // Events per wall second (by event time), pruned to the last few minutes.
  const perSecond = new Map<number, number>();
  let newestSecond = -Infinity;

  let queue: unknown[] = [];
  let scheduled = false;
  let snap: Snapshot = mk(0);
  const listeners = new Set<() => void>();

  function mk(version: number): Snapshot {
    return {
      version,
      events,
      counts: { ...counts },
      state: latestState,
      transitions,
      verdict,
      procs,
      iocs,
      ended,
      batchId,
      lastSeq,
      firstTs,
      lastTs,
      connection,
      retry,
    };
  }

  function publish() {
    snap = mk(snap.version + 1);
    listeners.forEach((l) => l());
  }

  function ioc(kind: IocPreviewKind, value: string, seq: number, source: string) {
    const v = value.trim();
    if (!v) return;
    const k = `${kind}\0${v}`;
    const cur = iocs.get(k);
    if (cur) {
      cur.hits++;
      if (!cur.sources.includes(source)) cur.sources.push(source);
    } else iocs.set(k, { kind, value: v, hits: 1, firstSeq: seq, sources: [source] });
  }

  function addr(v: unknown, seq: number, source: string) {
    const raw = str(v).trim();
    const host = raw.startsWith("[") ? raw.slice(1, raw.indexOf("]")) : raw.split(":").length === 2 ? raw.split(":")[0] : raw;
    if (IPV4.test(host)) ioc("ipv4", host, seq, source);
    else if (host.includes(":")) ioc("ipv6", host, seq, source);
  }

  function rebuildTransitions() {
    const out: Transition[] = [];
    for (const t of stateEvents) if (out[out.length - 1]?.state !== t.state) out.push(t);
    transitions = out;
  }

  function apply(raw: unknown, receivedAt: number): boolean {
    if (!sane(raw)) return false;
    const e: JobEvent = raw.fields && typeof raw.fields === "object" ? raw : { ...raw, fields: {} };
    if (seen.has(e.seq)) return false;
    seen.add(e.seq);
    if (events.length === 0 || e.seq > events[events.length - 1].seq) events.push(e);
    else {
      let lo = 0;
      let hi = events.length;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (events[mid].seq < e.seq) lo = mid + 1;
        else hi = mid;
      }
      events.splice(lo, 0, e);
    }
    if (e.seq > lastSeq) lastSeq = e.seq;
    counts[categoryOf(e.kind)]++;

    const ts = Date.parse(e.ts);
    if (Number.isFinite(ts)) {
      if (firstTs === null || ts < firstTs) firstTs = ts;
      if (lastTs === null || ts > lastTs) lastTs = ts;
      const eff = Math.min(ts, receivedAt);
      if (lastEventAt === null || eff > lastEventAt) lastEventAt = eff;
      if (eff > receivedAt - RATE_WINDOW_MS) recent.push(eff);
      const sec = Math.floor(ts / 1000);
      perSecond.set(sec, (perSecond.get(sec) ?? 0) + 1);
      if (sec > newestSecond) newestSecond = sec;
      if (perSecond.size > HISTOGRAM_KEEP * 2) {
        for (const k of perSecond.keys()) if (k < newestSecond - HISTOGRAM_KEEP) perSecond.delete(k);
      }
    }

    const f = e.fields;
    switch (e.kind) {
      case "job.state": {
        const state = str(f.state);
        const reason = str(f.reason);
        if (!latestState || e.seq > latestState.seq) latestState = { state, reason, seq: e.seq, ts };
        const known = asEffective(state);
        if (known) {
          const t: Transition = { state: known, ts, seq: e.seq, ...(reason ? { reason } : {}) };
          let i = stateEvents.length;
          while (i > 0 && stateEvents[i - 1].seq > e.seq) i--;
          stateEvents.splice(i, 0, t);
          rebuildTransitions();
        }
        break;
      }
      case "verdict": {
        // Mirror the backend store's max-score merge (see
        // UpdateJobVerdict / UpdateJobState in backend/internal/store).
        // Two publishers race on this topic — the yara-driven
        // verdict-scorer promotes to "nanocore" / "suspicious" as
        // evidence lands, and the controller's behavior analyzer
        // emits a terminal verdict that is often "benign" when a
        // sample (NanoCore RAT, say) doesn't trip our classic
        // injection tags. Taking strictly the HIGHEST seq let the
        // benign one clobber nanocore on reload, so the UI flashed
        // malicious → benign a second after mount. Max score wins;
        // seq is only the tiebreaker.
        const next = verdictFromEvent(f, e.seq);
        if (!verdict || next.score > verdict.score || (next.score === verdict.score && e.seq > verdict.seq)) {
          verdict = next;
        }
        break;
      }
      case "proc.start": {
        const pid = Number(f.pid);
        const ppid = Number(f.ppid);
        procs.set(pid, { pid, ppid, image: str(f.image), cmdline: str(f.cmdline), startSeq: e.seq, children: procs.get(pid)?.children ?? [] });
        const parent = procs.get(ppid);
        if (parent && !parent.children.includes(pid)) parent.children.push(pid);
        break;
      }
      case "proc.exit": {
        const p = procs.get(Number(f.pid));
        if (p) p.exitCode = Number(f.exit_code);
        break;
      }
      case "net.dns":
        ioc("domain", str(f.qname ?? f.name).replace(/\.$/, ""), e.seq, e.kind);
        if (f.ip != null) addr(f.ip, e.seq, e.kind);
        break;
      case "net.flow":
      case "net.conn":
        addr(f.dst, e.seq, e.kind);
        break;
      case "net.http": {
        const host = str(f.host);
        ioc("domain", host.replace(/:\d+$/, ""), e.seq, e.kind);
        if (host && f.path != null) ioc("url", `${str(f.scheme) || "http"}://${host}${str(f.path)}`, e.seq, e.kind);
        break;
      }
      case "file.create":
      case "file.write":
      case "file.delete":
      case "file.drop":
        ioc("file_path", str(f.path), e.seq, e.kind);
        break;
      case "file.rename":
        ioc("file_path", str(f.to), e.seq, e.kind);
        break;
      case "registry.set":
      case "registry.delete":
        ioc("registry_key", str(f.key), e.seq, e.kind);
        break;
      case "mutex.create":
        ioc("mutex", str(f.name), e.seq, e.kind);
        break;
      case "image.load":
        ioc("sha256", str(f.sha256), e.seq, e.kind);
        break;
      case "config.extracted": {
        const list = (v: unknown) => (Array.isArray(v) ? v.map(str) : []);
        for (const c of list(f.c2)) {
          const host = c.trim().replace(/^[a-z][a-z0-9+.-]*:\/\//i, "").split("/")[0].replace(/:\d+$/, "");
          if (IPV4.test(host)) ioc("ipv4", host, e.seq, e.kind);
          else if (host) ioc("domain", host, e.seq, e.kind);
          if (/^[a-z][a-z0-9+.-]*:\/\//i.test(c.trim())) ioc("url", c, e.seq, e.kind);
        }
        for (const u of list(f.urls)) ioc("url", u, e.seq, e.kind);
        for (const m of list(f.mutex)) ioc("mutex", m, e.seq, e.kind);
        for (const p of list(f.paths)) ioc("file_path", p, e.seq, e.kind);
        break;
      }
    }
    return true;
  }

  function flush() {
    scheduled = false;
    if (queue.length === 0) return;
    const batch = queue;
    queue = [];
    const receivedAt = now();
    let added = 0;
    for (const e of batch) if (apply(e, receivedAt)) added++;
    if (added === 0) return; // a replay of known seqs: no new batch, no pip
    batchId++;
    publish();
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    if (opts.schedule) opts.schedule(flush);
    else if (typeof document !== "undefined" && document.hidden) setTimeout(flush, 250);
    else if (typeof requestAnimationFrame === "function") requestAnimationFrame(flush);
    else setTimeout(flush, 0);
  }

  function prune(at: number) {
    let i = 0;
    while (i < recent.length && recent[i] <= at - RATE_WINDOW_MS) i++;
    if (i) recent.splice(0, i);
  }

  // Read-time values (no re-render of their own: read them under useClock).
  const rate = {
    get perSec(): number {
      prune(now());
      return recent.length / (RATE_WINDOW_MS / 1000);
    },
    get lastEventAt(): number | null {
      return lastEventAt;
    },
    /** Events per wall second over the `secs` whole seconds before `now` (oldest first), by event time. */
    series(at: number, secs = 60): number[] {
      const n = Math.max(1, Math.floor(secs));
      const end = Math.floor(at / 1000);
      const out = new Array<number>(n);
      for (let i = 0; i < n; i++) out[i] = perSecond.get(end - n + i) ?? 0;
      return out;
    },
  };

  return {
    push(e: unknown) {
      queue.push(e);
      schedule();
    },
    /** The run is over: mark ended (after applying anything queued). */
    end() {
      if (queue.length) flush();
      if (ended) return;
      ended = true;
      connection = "closed";
      retry = null;
      publish();
    },
    setConnection(c: StreamConnection, r: Snapshot["retry"] = null) {
      if (c === connection && r?.at === retry?.at && r?.attempt === retry?.attempt) return;
      connection = c;
      retry = r;
      publish();
    },
    subscribe(l: () => void) {
      listeners.add(l);
      return () => {
        listeners.delete(l);
      };
    },
    getSnapshot: (): Snapshot => snap,
    flushNow: flush,
    rate,
  };
}
export type EventStore = ReturnType<typeof createEventStore>;


// ---- effective state (§12.1) ------------------------------------------------

type Effective = {
  state: EffectiveState;
  stateSource: "stream" | "record";
  reason?: string;
  verdict: VerdictSnap | null;
  live: boolean;
  ended: boolean;
  t0: number | null;
  t0Source: "running" | "booting" | null;
  /** job.state transitions; steps only the record knows are appended with source "record". */
  transitions: readonly Transition[];
  /** The record disagrees with the stream (ahead/behind ranks); `since` = when the stream moved. */
  disagree: { recordState: JobState; since: number } | null;
};

const rank = (s: string): number => (s === "queued" ? 0 : isTerminal(s) ? 2 : 1);
const tsOf = (iso: string | undefined): number => (iso ? Date.parse(iso) : NaN);

/** Lifecycle steps from the record alone: submitted → dispatched → its terminal state. */
function recordTransitions(record: Job, after: readonly Transition[]): Transition[] {
  const out: Transition[] = [];
  if (after.length === 0) {
    out.push({ state: "queued", ts: tsOf(record.submitted_at), seq: 0, source: "record" });
    if (record.dispatched_at) out.push({ state: "dispatched", ts: tsOf(record.dispatched_at), seq: 0, source: "record" });
  }
  if (isTerminal(record.state) && after[after.length - 1]?.state !== record.state) {
    out.push({ state: record.state, ts: tsOf(record.ended_at), seq: 0, ...(record.reason ? { reason: record.reason } : {}), source: "record" });
  }
  return out;
}

/** The stream beats the (lagging) record: state is the latest job.state event. The record speaks
 *  before any event arrives, and when it is terminal while the stream is not (a terminal record is
 *  final: the stream is still replaying, or it stalled before its last events). */
export function deriveEffective(snap: Snapshot, record?: Job | null): Effective {
  const streamState = asEffective(snap.state?.state);
  const recordFinal = !!record && isTerminal(record.state);
  let state: EffectiveState = "queued";
  let stateSource: Effective["stateSource"] = "record";
  let reason: string | undefined;
  if (streamState && !(recordFinal && !TERMINAL.has(streamState))) {
    state = streamState;
    stateSource = "stream";
    reason = snap.state?.reason || undefined;
  } else if (record) {
    state = record.state;
    reason = record.reason || undefined;
  }

  const verdict = snap.verdict ?? (record?.verdict ? verdictFromRecord(record.verdict) : null);
  const ended = snap.ended;
  const live = !ended && !TERMINAL.has(state);

  const transitions =
    stateSource === "record" && record ? [...snap.transitions, ...recordTransitions(record, snap.transitions)] : snap.transitions;

  const running = snap.transitions.find((t) => t.state === "running");
  const booting = running ? undefined : snap.transitions.find((t) => t.state === "booting");
  const t0Ev = running ?? booting;
  const t0 = t0Ev && Number.isFinite(t0Ev.ts) ? t0Ev.ts : null;

  let disagree: Effective["disagree"] = null;
  if (record && stateSource === "stream" && snap.state) {
    const r = rank(record.state);
    const s = rank(state);
    if (s > r || (s === 2 && r === 2 && state !== record.state)) disagree = { recordState: record.state, since: snap.state.ts };
  }

  return {
    state,
    stateSource,
    reason,
    verdict,
    live,
    ended,
    t0,
    t0Source: t0 === null ? null : running ? "running" : "booting",
    transitions,
    disagree,
  };
}
